import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isValidEmailOtp } from '../lib/bookingService';
import {
  PASSWORD_REQUIREMENTS_HELPER,
  validateResetPasswordForm,
} from '../lib/passwordPolicy';
import {
  RECOVERY_CODE_SENT_MESSAGE,
  RESEND_COOLDOWN_SECONDS,
  getPasswordResetRedirectUrl,
  isValidResetEmail,
  mapPasswordResetRequestError,
  mapRecoveryOtpError,
  mapRecoveryPasswordUpdateError,
  shouldAdvanceAfterRecoveryRequest,
} from '../lib/passwordReset';
import { supabase } from '../lib/supabaseClient';

const OTP_LENGTH = 6;
const emptyDigits = () => Array.from({ length: OTP_LENGTH }, () => '');

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState(emptyDigits);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [success, setSuccess] = useState('');
  const [resendSeconds, setResendSeconds] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  const startResendCooldown = () => {
    setResendSeconds(RESEND_COOLDOWN_SECONDS);
  };

  const requestRecoveryCode = async (targetEmail) => {
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(targetEmail, {
      redirectTo: getPasswordResetRedirectUrl(),
    });
    return resetError || null;
  };

  const handleSendEmail = async (event) => {
    event.preventDefault();
    if (loading) return;

    const targetEmail = email.trim();
    setError('');
    setInfo('');
    setSuccess('');

    if (!isValidResetEmail(targetEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const resetError = await requestRecoveryCode(targetEmail);
      if (!shouldAdvanceAfterRecoveryRequest(resetError)) {
        setError(mapPasswordResetRequestError(resetError));
        return;
      }

      setEmail(targetEmail);
      setDigits(emptyDigits());
      setInfo(RECOVERY_CODE_SENT_MESSAGE);
      setStep('code');
      startResendCooldown();
    } catch (sendErr) {
      if (!shouldAdvanceAfterRecoveryRequest(sendErr)) {
        setError(mapPasswordResetRequestError(sendErr));
        return;
      }
      setEmail(targetEmail);
      setDigits(emptyDigits());
      setInfo(RECOVERY_CODE_SENT_MESSAGE);
      setStep('code');
      startResendCooldown();
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    if (loading) return;

    const token = digits.join('');
    setError('');
    setSuccess('');

    if (!token) {
      setError('Please enter the verification code.');
      return;
    }

    if (!isValidEmailOtp(token)) {
      setError('Please enter the verification code.');
      return;
    }

    setLoading(true);
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'recovery',
      });
      if (verifyError) throw verifyError;

      setDigits(emptyDigits());
      setInfo('');
      setStep('password');
    } catch (verifyErr) {
      setError(mapRecoveryOtpError(verifyErr));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (loading || resendSeconds > 0) return;
    setError('');
    setLoading(true);
    try {
      const resetError = await requestRecoveryCode(email);
      if (!shouldAdvanceAfterRecoveryRequest(resetError)) {
        setError(mapPasswordResetRequestError(resetError));
        return;
      }
      setInfo(RECOVERY_CODE_SENT_MESSAGE);
      startResendCooldown();
    } catch (sendErr) {
      if (!shouldAdvanceAfterRecoveryRequest(sendErr)) {
        setError(mapPasswordResetRequestError(sendErr));
        return;
      }
      setInfo(RECOVERY_CODE_SENT_MESSAGE);
      startResendCooldown();
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    if (loading) return;

    setError('');
    setSuccess('');
    const validationError = validateResetPasswordForm({ newPassword, confirmPassword });
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      await supabase.auth.signOut();
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Your password has been changed successfully.');
      window.setTimeout(() => navigate('/login', { replace: true }), 1600);
    } catch (updateErr) {
      setError(mapRecoveryPasswordUpdateError(updateErr));
    } finally {
      setLoading(false);
    }
  };

  const applyDigitValue = (index, rawValue) => {
    const numeric = String(rawValue || '').replace(/\D/g, '');
    if (!numeric) {
      setDigits((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }

    if (numeric.length > 1) {
      const pasted = numeric.slice(0, OTP_LENGTH).split('');
      setDigits((prev) => {
        const next = [...prev];
        pasted.forEach((digit, offset) => {
          next[offset] = digit;
        });
        return next;
      });
      const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
      inputRefs.current[focusIndex]?.focus();
      return;
    }

    setDigits((prev) => {
      const next = [...prev];
      next[index] = numeric.slice(-1);
      return next;
    });
    if (index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const title = step === 'email'
    ? 'Forgot Password'
    : step === 'code'
      ? 'Verification Code'
      : 'New Password';

  const subtitle = step === 'email'
    ? "Provide your account's email to which you want to reset your password"
    : step === 'code'
      ? 'Enter verification code'
      : 'Enter new password';

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#f4f2ef]">
      <div className="hidden md:flex md:w-1/2 bg-black items-center justify-center p-12">
        <img
          src="/assets/echojham_logos/echojham.logo3.png"
          alt="Echo Jham"
          className="w-[280px] max-w-full h-auto select-none"
        />
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center px-6 py-10 md:px-12">
        <div className="w-full max-w-[420px]">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mb-6 inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[#5f6b7d] hover:text-[#0f2746] transition"
          >
            <ArrowLeft size={14} /> Back to Login
          </button>

          <div className="mb-8">
            <h1 className="text-3xl md:text-[2.15rem] font-black text-[#0f2746] tracking-tighter uppercase italic leading-none">
              {title}
            </h1>
            <p className="mt-3 text-sm font-medium text-[#5f6b7d]">{subtitle}</p>
          </div>

          {step === 'email' ? (
            <form onSubmit={handleSendEmail} className="space-y-4" noValidate>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email Address"
                autoComplete="email"
                disabled={loading}
                className="w-full px-4 py-4 rounded-2xl border border-[#d8dee8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#8b0000]/15 focus:border-[#8b0000] text-[#0f2746] placeholder-[#a8b0c0] disabled:opacity-60"
              />
              <Feedback error={error} info={info} success={success} />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#8b0000] hover:bg-[#6f0000] text-white py-4 rounded-full text-xs font-black tracking-[0.18em] uppercase shadow-[0_16px_30px_rgba(139,0,0,0.18)] transition active:scale-95 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send'}
              </button>
            </form>
          ) : null}

          {step === 'code' ? (
            <form onSubmit={handleVerifyCode} className="space-y-4" noValidate>
              <div className="flex justify-between gap-2">
                {digits.map((digit, index) => (
                  <input
                    key={`otp-${index}`}
                    ref={(node) => {
                      inputRefs.current[index] = node;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={index === 0 ? OTP_LENGTH : 1}
                    value={digit}
                    onChange={(event) => applyDigitValue(index, event.target.value)}
                    onKeyDown={(event) => handleDigitKeyDown(index, event)}
                    disabled={loading}
                    aria-label={`Digit ${index + 1}`}
                    className="h-12 w-12 min-w-0 rounded-xl border border-[#d8dee8] bg-white text-center text-lg font-semibold text-[#0f2746] focus:outline-none focus:ring-2 focus:ring-[#8b0000]/15 focus:border-[#8b0000] disabled:opacity-60"
                  />
                ))}
              </div>
              <Feedback error={error} info={info} success={success} />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#8b0000] hover:bg-[#6f0000] text-white py-4 rounded-full text-xs font-black tracking-[0.18em] uppercase shadow-[0_16px_30px_rgba(139,0,0,0.18)] transition active:scale-95 disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading || resendSeconds > 0}
                className="w-full text-xs font-bold uppercase tracking-wide text-[#5f6b7d] hover:text-[#0f2746] transition disabled:opacity-50"
              >
                {resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : 'Resend Code'}
              </button>
            </form>
          ) : null}

          {step === 'password' ? (
            <form onSubmit={handleChangePassword} className="space-y-4" noValidate>
              <PasswordField
                placeholder="New Password"
                value={newPassword}
                onChange={setNewPassword}
                shown={showPassword}
                toggle={() => setShowPassword((prev) => !prev)}
                disabled={loading}
              />
              <PasswordField
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                shown={showConfirmPassword}
                toggle={() => setShowConfirmPassword((prev) => !prev)}
                disabled={loading}
              />
              <p className="text-[11px] text-[#5f6b7d]">{PASSWORD_REQUIREMENTS_HELPER}</p>
              <Feedback error={error} info={info} success={success} />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#8b0000] hover:bg-[#6f0000] text-white py-4 rounded-full text-xs font-black tracking-[0.18em] uppercase shadow-[0_16px_30px_rgba(139,0,0,0.18)] transition active:scale-95 disabled:opacity-50"
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const Feedback = ({ error, info, success }) => (
  <>
    {error ? <p className="text-xs font-medium text-red-600">{error}</p> : null}
    {!error && info ? <p className="text-xs font-medium text-[#5f6b7d]">{info}</p> : null}
    {success ? <p className="text-xs font-medium text-emerald-700">{success}</p> : null}
  </>
);

const PasswordField = ({ placeholder, value, onChange, shown, toggle, disabled }) => (
  <div className="relative">
    <input
      type={shown ? 'text' : 'password'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      autoComplete="new-password"
      disabled={disabled}
      className="w-full px-4 py-4 pr-12 rounded-2xl border border-[#d8dee8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#8b0000]/15 focus:border-[#8b0000] text-[#0f2746] placeholder-[#a8b0c0] disabled:opacity-60"
    />
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9ba3b3] hover:text-[#0f2746]"
      aria-label={shown ? `Hide ${placeholder}` : `Show ${placeholder}`}
    >
      {shown ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
);
